import { PitchListDialog } from 'enums/pitch-lists';
import { QueueType } from 'interfaces/i-queue-mode';
import { PLStoreMode } from 'lib_ts/enums/pitch-list.enums';
import { ForUpdate, Nullable } from 'lib_ts/interfaces/common/i-generics';
import { ISortSelection } from 'lib_ts/interfaces/common/i-option';
import { IPitchList } from 'lib_ts/interfaces/pitches';
import { IFilterPitches } from 'lib_ts/interfaces/pitches/i-filter-pitches';
import { IPitch, ITrainingPitch } from 'lib_ts/interfaces/pitches/i-pitch';
import { IPitchMetricsFilters } from 'lib_ts/interfaces/pitches/i-pitch-metrics-filters';
import { ISearchPitches } from 'lib_ts/interfaces/pitches/i-search-pitches';

// 0 => no limit
export const MAX_SEARCH_LIMIT = 0;

// on first load of Library, limit to this number before loading the remainder in the background
export const INITIAL_LIBRARY_SEARCH_LIMIT = 500;
export const SEARCH_ID = '--SEARCH--';

interface ListState {
  pitches: IPitch[];

  selectedPitch: IPitch | undefined;

  // to prevent race condition edge case where the user has switched lists but an earlier request returns later
  // e.g. user attempts to load Library, then quickly switches to Personal List before the Library request returns
  pitchListRequestId: number | undefined;
}

interface ListActions {
  // undefined => unselect
  setSelectedPitch: (v: IPitch | undefined) => void;

  setPitches: (v: IPitch[]) => void;

  updatePitches: (config: {
    payloads: ForUpdate<IPitch>[];
    successMsg?: string;
  }) => Promise<IPitch[] | undefined>;

  deletePitches: (ids: string[], silently?: boolean) => Promise<boolean>;

  reloadPitches: (config: {
    activeList: IPitchList | undefined;
  }) => Promise<void>;

  // uses the latest TMS values to update every pitch in list
  mixinTms: () => void;
}

interface FilterState {
  filters: IFilterPitches;
  metricsFilters: IPitchMetricsFilters | null;
  filteredPitches: IPitch[];
}

interface FilterActions {
  mergeFilters: (v: Partial<IFilterPitches>) => void;
  setMetricsFilters: (v: IPitchMetricsFilters | null) => void;
  setFilteredPitches: (v: IPitch[]) => void;
  resetFilters: () => void;
}

interface SearchState {
  sortMode: Nullable<ISortSelection>;
  defaultSearchCriteria: ISearchPitches;
  searchCriteria: ISearchPitches;
}

interface SearchActions {
  setSortMode: (v: Nullable<ISortSelection>) => void;

  // should be triggered 1x whenever pitch list changes or visiting pitch library, used for reset behaviour
  resetSearchCriteria: (config: {
    // undefined => don't change current value
    mode?: PLStoreMode;
    // undefined => don't change default value
    criteria?: ISearchPitches;
  }) => Promise<void>;

  mergeSearchCriteria: (config: {
    // undefined => repeat search w/o changing criteria
    criteria?: Partial<ISearchPitches>;
  }) => Promise<void>;
}

interface QueueState {
  queueType: QueueType;
  queuePitchIDs: string[];
}

interface QueueActions {
  setQueueType: (v: QueueType) => void;
  setQueuePitchIDs: (v: string[]) => void;
}

interface DialogState {
  dialog:
    | Partial<{
        // when the dialog was (re)opened
        key: number;
        // which dialog is open
        mode: PitchListDialog;
        // The specific pitch(es) used within the dialogs
        pitches: IPitch[];
        // The specific pitch(es) used within the training dialog
        trainingPitches: ITrainingPitch[];
      }>
    | undefined;
}

interface DialogActions {
  openDialog: (args: {
    dialog: PitchListDialog;
    pitches?: IPitch[];
    trainingPitches?: ITrainingPitch[];
    calledBy?: string;
  }) => void;

  setDialogPitches: (pitches: IPitch[]) => void;

  closeDialog: () => void;
}

interface BaseSlice {
  showRecap: boolean;

  mode: PLStoreMode;
  setMode: (v: PLStoreMode) => void;

  loading: boolean;
  setLoading: (loading: boolean) => void;
}

export type PitchListStore = ListState &
  ListActions &
  SearchState &
  SearchActions &
  FilterState &
  FilterActions &
  QueueState &
  QueueActions &
  DialogState &
  DialogActions &
  BaseSlice;
